<script setup>
    import { computed, inject } from 'vue';
    import SubHead from '@/components/result/MLSRD-2026/SubHead.vue';
    import LevelRatio from '@/components/result/MLSRD-2026/LevelRatio.vue';
    import SubArea from '@/components/result/MLSRD-2026/SubArea.vue';
    import AnswerRates from '@/components/result/MLSRD-2026/AnswerRates.vue';

    // 부모에서 주입받은 데이터
    const classData = inject('classData');
    const loading = inject('loading');

    const pagetype = "numeracy";
    const region = "seoul";

    // 학교급별 점수 구간 (표시용)
    const getScoreRanges = (schulType, gradeCode) => {
        const key = `${schulType}${gradeCode}`;
        const scoreRanges = {
            'E4': { '4': '1450~1700', '3': '1342~1449', '2': '1226~1341', '1': '1000~1225' },
            'E6': { '4': '1542~1800', '3': '1388~1541', '2': '1295~1387', '1': '1050~1294' },
            'M2': { '4': '1621~1900', '3': '1501~1620', '2': '1388~1500', '1': '1100~1387' },
            'H1': { '4': '1697~2000', '3': '1574~1696', '2': '1436~1573', '1': '1150~1435' }
        };
        return scoreRanges[key] || scoreRanges['E4'];
    };

    // 수준별 학생 분류 및 테이블 데이터 (matlevel 기반)
    const rateTableData = computed(() => {
        if (!classData.value || !classData.value.list) {
            const defaultScoreRanges = getScoreRanges('E', '4');
            return [
                { level: '4', scoreRange: defaultScoreRanges['4'], students: '' },
                { level: '3', scoreRange: defaultScoreRanges['3'], students: '' },
                { level: '2', scoreRange: defaultScoreRanges['2'], students: '' },
                { level: '1', scoreRange: defaultScoreRanges['1'], students: '' }
            ];
        }

        const data = classData.value;
        const scoreRanges = getScoreRanges(data.schul_type || 'E', data.grade_code || '4');
        
        // 각 수준별로 학생들을 분류 (matlevel 사용)
        const levelStudents = {
            '4': [],
            '3': [],
            '2': [],
            '1': []
        };

        data.list.forEach(student => {
            // matlevel이 없는 학생은 수리력 시험을 안친 것으로 간주하여 제외
            if (!student.matlevel) {
                return; // matlevel이 없으면 제외
            }
            
            const matLevel = String(student.matlevel);
            
            if (levelStudents[matLevel]) {
                levelStudents[matLevel].push(student.name || '');
            }
        });

        return ['4', '3', '2', '1'].map(level => ({
            level: level,
            scoreRange: scoreRanges[level],
            students: levelStudents[level].join(', ')
        }));
    });

    // 수리력 수준별 차트 데이터 (matlevel 기반)
    const numeracyChartData = computed(() => {
        if (!classData.value || !classData.value.list) {
            return [
                { level: '1수준', percentage: 0, count: 0 },
                { level: '2수준', percentage: 0, count: 0 },
                { level: '3수준', percentage: 0, count: 0 },
                { level: '4수준', percentage: 0, count: 0 }
            ];
        }

        const data = classData.value;
        
        // matlevel별 학생 수 계산 (matlevel이 있는 학생만 포함)
        const levelCounts = { 1: 0, 2: 0, 3: 0, 4: 0 };
        let totalStudentsWithMatlevel = 0;
        
        data.list.forEach(student => {
            // matlevel이 없는 학생은 수리력 시험을 안친 것으로 간주하여 제외
            if (!student.matlevel) {
                return;
            }
            
            totalStudentsWithMatlevel++;
            const matLevel = parseInt(student.matlevel);
            if (levelCounts[matLevel] !== undefined) {
                levelCounts[matLevel]++;
            }
        });

        return [
            { 
                level: '1수준', 
                percentage: totalStudentsWithMatlevel > 0 ? Math.round((levelCounts[1] / totalStudentsWithMatlevel) * 100 * 10) / 10 : 0,
                count: levelCounts[1]
            },
            { 
                level: '2수준', 
                percentage: totalStudentsWithMatlevel > 0 ? Math.round((levelCounts[2] / totalStudentsWithMatlevel) * 100 * 10) / 10 : 0,
                count: levelCounts[2]
            },
            { 
                level: '3수준', 
                percentage: totalStudentsWithMatlevel > 0 ? Math.round((levelCounts[3] / totalStudentsWithMatlevel) * 100 * 10) / 10 : 0,
                count: levelCounts[3]
            },
            { 
                level: '4수준', 
                percentage: totalStudentsWithMatlevel > 0 ? Math.round((levelCounts[4] / totalStudentsWithMatlevel) * 100 * 10) / 10 : 0,
                count: levelCounts[4]
            }
        ];
    });

    // 우리 반 수리력 영역별 정답률 (matsub1~4rate)
    const ourClassPercentages = computed(() => {
        if (!classData.value) {
            return [75, 77, 73, 83]; // 기본값
        }
        
        const data = classData.value;
        return [
            parseInt(data.matsub1rate) || 0,  // 수와 양
            parseInt(data.matsub2rate) || 0,  // 규칙과 관계
            parseInt(data.matsub3rate) || 0,  // 모양과 측정
            parseInt(data.matsub4rate) || 0   // 자료와 불확실성
        ];
    });

    // 전체 평균 수리력 영역별 정답률 (matsub1~4_total_rate)
    const overallPercentages = computed(() => {
        if (!classData.value) {
            return [52, 50, 77, 100]; // 기본값
        }
        
        const data = classData.value;
        return [
            parseInt(data.matsub1_total_rate) || 0,
            parseInt(data.matsub2_total_rate) || 0,
            parseInt(data.matsub3_total_rate) || 0,
            parseInt(data.matsub4_total_rate) || 0
        ];
    });
</script>

<template>
    <div class="slide-inner">
       <div class="content">
            <SubHead :type="pagetype" :region="region" title="수리력"/>    
            <div class="card-wrap">
                <LevelRatio
                    :type="pagetype"                    
                    :table-data="rateTableData"
                    :chart-data="numeracyChartData"
                />
                <SubArea :type="pagetype"/>
                <AnswerRates
                    :type="pagetype"    
                    :our-class-percentages="ourClassPercentages"
                    :overall-percentages="overallPercentages"  
                />
            </div>
       </div>
    </div>
</template>